import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { isDeciduousTooth, isUpperTooth } from '../../constants/odontology';
import {
  DECIDUOUS_SCALE,
  IMPLANT_SHAPE,
  POST_SHAPE,
  getToothAnatomy,
} from '../../constants/toothAnatomy';
import { buildToothLateralView, type ToothFill, type ToothView } from '../../utils/dentalChartView';

/**
 * Perfil lateral de um dente, com os achados que só existem abaixo da coroa:
 * endodontia, lesão periapical, implante, pino e prótese.
 *
 * Complementa a vista oclusal — esta mostra o eixo longo do dente, aquela
 * mostra as faces. Juntas cobrem tudo que o odontograma precisa registrar.
 *
 * O desenho-base é sempre o de um dente superior direito; as demais
 * hemiarcadas são reflexões, de modo que mesial e distal caiam sempre do lado
 * correto em relação à linha média.
 */

/**
 * Altura do glifo em px. A largura acompanha a proporção de cada dente, e o
 * viewBox é recortado na mesma proporção — assim a escala vertical fica
 * idêntica em toda a arcada e a linha cervical dos 16 dentes se alinha, apesar
 * de um molar ser três vezes mais largo que um incisivo lateral.
 */
const GLYPH_HEIGHT = 58;
const BASE_WIDTH = 30;

const ENAMEL = '#FFFFFF';
const DENTIN = '#F8FAFC';
const OUTLINE = '#94A3B8';
const PULP_OUTLINE = '#CBD5E1';

const HATCHED_STATUSES = new Set(['planned', 'in_progress']);

const RIGHT_SIDE_QUADRANTS = new Set(['1', '4', '5', '8']);

export interface ToothLateralGlyphProps {
  toothNumber: string;
  view: ToothView;
  selected?: boolean;
  disabled?: boolean;
  onSelectTooth?: (toothNumber: string) => void;
}

const ToothLateralGlyph: React.FC<ToothLateralGlyphProps> = ({
  toothNumber,
  view,
  selected = false,
  disabled = false,
  onSelectTooth,
}) => {
  const anatomy = getToothAnatomy(toothNumber);
  const lateral = useMemo(() => buildToothLateralView(view), [view]);

  const upper = isUpperTooth(toothNumber);
  const patientRightSide = RIGHT_SIDE_QUADRANTS.has(toothNumber.charAt(0));
  const deciduous = isDeciduousTooth(toothNumber);

  // Arcada inferior espelha no eixo Y (raiz para baixo); hemiarcada esquerda
  // espelha no eixo X para manter mesial voltado à linha média.
  const transform = [
    patientRightSide ? '' : 'scaleX(-1)',
    upper ? '' : 'scaleY(-1)',
  ].filter(Boolean).join(' ');

  const scale = deciduous ? DECIDUOUS_SCALE : 1;
  const width = Math.round(BASE_WIDTH * anatomy.widthRatio * scale);
  const height = Math.round(GLYPH_HEIGHT * scale);

  const viewWidth = 100 * anatomy.widthRatio;
  const viewBox = `${(100 - viewWidth) / 2} 0 ${viewWidth} 200`;

  // Ids de pattern são globais no documento, então carregam o número do dente.
  const hatchColors = useMemo(() => {
    const colors = new Set<string>();
    Object.values(lateral).forEach((value) => {
      const fill = value as ToothFill | null;
      if (fill && typeof fill === 'object' && HATCHED_STATUSES.has(fill.status)) colors.add(fill.color);
    });
    return Array.from(colors);
  }, [lateral]);

  const hatchId = (color: string) => `lat-hatch-${toothNumber}-${color.replace('#', '')}`;
  const paint = (fill: ToothFill) =>
    HATCHED_STATUSES.has(fill.status) ? `url(#${hatchId(fill.color)})` : fill.color;

  const faded = view.absent || lateral.extractionPlanned;
  const showRoots = !lateral.implant;
  const showCrown = !lateral.rootRemnant;

  return (
    <Box
      component="svg"
      viewBox={viewBox}
      role="presentation"
      onClick={() => !disabled && onSelectTooth?.(toothNumber)}
      sx={{
        width,
        height,
        display: 'block',
        overflow: 'visible',
        cursor: disabled || !onSelectTooth ? 'default' : 'pointer',
        opacity: faded ? 0.35 : 1,
        transition: 'opacity 0.15s ease',
      }}
      style={{ transform, transformOrigin: 'center center' }}
    >
      {hatchColors.length > 0 && (
        <defs>
          {hatchColors.map((color) => (
            <pattern
              key={color}
              id={hatchId(color)}
              patternUnits="userSpaceOnUse"
              width="10"
              height="10"
              patternTransform="rotate(45)"
            >
              <rect width="10" height="10" fill={ENAMEL} />
              <line x1="0" y1="0" x2="0" y2="10" stroke={color} strokeWidth="5" />
            </pattern>
          ))}
        </defs>
      )}

      {/* Raízes, atrás da coroa para que o colo cubra a emenda. */}
      {showRoots && anatomy.roots.map((d, index) => (
        <path key={`root-${index}`} d={d} fill={DENTIN} stroke={OUTLINE} strokeWidth={2.5} strokeLinejoin="round" />
      ))}

      {/* Implante: a fixação ocupa o lugar da raiz. */}
      {lateral.implant && (
        <g>
          <path
            d={IMPLANT_SHAPE.fixture}
            fill={paint(lateral.implant)}
            stroke={lateral.implant.color}
            strokeWidth={2.5}
            strokeLinejoin="round"
          />
          {IMPLANT_SHAPE.threads.map((d, index) => (
            <path key={`thread-${index}`} d={d} stroke={ENAMEL} strokeWidth={2.5} strokeLinecap="round" opacity={0.75} />
          ))}
          <path d={IMPLANT_SHAPE.abutment} fill={lateral.implant.color} opacity={0.85} />
        </g>
      )}

      {/* Lesão periapical: halo centrado no ápice de cada raiz. */}
      {lateral.periapical && anatomy.apices.map((apex, index) => (
        <circle
          key={`periapical-${index}`}
          cx={apex.x}
          cy={apex.y}
          r={13}
          fill={paint(lateral.periapical!)}
          fillOpacity={0.55}
          stroke={lateral.periapical!.color}
          strokeWidth={2.5}
        />
      ))}

      {/* Coroa anatômica. */}
      {showCrown && (
        <path d={anatomy.crown} fill={ENAMEL} stroke={OUTLINE} strokeWidth={2.5} strokeLinejoin="round" />
      )}

      {/* Prótese: preenche o perfil inteiro da coroa. */}
      {showCrown && lateral.crownProsthesis && (
        <path
          d={anatomy.crown}
          fill={paint(lateral.crownProsthesis)}
          stroke={lateral.crownProsthesis.color}
          strokeWidth={2.5}
          strokeLinejoin="round"
        />
      )}

      {/* Câmara e canais. Preenchidos quando há endodontia; apenas contornados
          quando o dente está hígido, para dar referência do eixo do canal. */}
      {!lateral.implant && (
        <g>
          {showCrown && (
            <path
              d={anatomy.pulpChamber}
              fill={lateral.endo ? paint(lateral.endo) : 'none'}
              stroke={lateral.endo ? lateral.endo.color : PULP_OUTLINE}
              strokeWidth={lateral.endo ? 2 : 1.5}
              strokeLinejoin="round"
            />
          )}
          {anatomy.canals.map((d, index) => (
            <path
              key={`canal-${index}`}
              d={d}
              fill="none"
              stroke={lateral.endo ? lateral.endo.color : PULP_OUTLINE}
              strokeWidth={lateral.endo ? 5 : 2}
              strokeLinecap="round"
              strokeDasharray={lateral.endo && HATCHED_STATUSES.has(lateral.endo.status) ? '7 5' : undefined}
            />
          ))}
        </g>
      )}

      {/* Núcleo/pino, sobre o canal. */}
      {lateral.post && (
        <path d={POST_SHAPE} fill={paint(lateral.post)} stroke={lateral.post.color} strokeWidth={2} strokeLinejoin="round" />
      )}

      {/* Bráquete ou contenção na vestibular, à altura da coroa. */}
      {lateral.orthodontic && showCrown && (
        <g>
          <rect
            x={40}
            y={anatomy.cej + 26}
            width={20}
            height={14}
            rx={3}
            fill={paint(lateral.orthodontic)}
            stroke={lateral.orthodontic.color}
            strokeWidth={2}
          />
          <line
            x1={26}
            y1={anatomy.cej + 33}
            x2={74}
            y2={anatomy.cej + 33}
            stroke={lateral.orthodontic.color}
            strokeWidth={3}
            strokeLinecap="round"
          />
        </g>
      )}

      {/* Fratura: traço em ziguezague cruzando a coroa. */}
      {lateral.fracture && showCrown && (
        <path
          d={`M 36,${anatomy.cej + 8} L 52,${anatomy.cej + 22} L 40,${anatomy.cej + 36} L 58,${anatomy.cej + 52}`}
          fill="none"
          stroke={lateral.fracture.color}
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Ausência confirmada ou extração indicada. */}
      {(view.absent || lateral.extractionPlanned) && (
        <g>
          <line
            x1={20} y1={30} x2={80} y2={180}
            stroke={view.absent ? '#475569' : '#DC2626'}
            strokeWidth={5}
            strokeLinecap="round"
            strokeDasharray={lateral.extractionPlanned && !view.absent ? '10 7' : undefined}
          />
          <line
            x1={80} y1={30} x2={20} y2={180}
            stroke={view.absent ? '#475569' : '#DC2626'}
            strokeWidth={5}
            strokeLinecap="round"
            strokeDasharray={lateral.extractionPlanned && !view.absent ? '10 7' : undefined}
          />
        </g>
      )}

      {selected && (
        <rect
          x={(100 - viewWidth) / 2 + 2}
          y={2}
          width={viewWidth - 4}
          height={196}
          rx={8}
          fill="none"
          stroke="#0284C7"
          strokeWidth={3}
          pointerEvents="none"
        />
      )}
    </Box>
  );
};

export default ToothLateralGlyph;
